import React, { useEffect, useMemo, useState } from "react";
import {
  Wallet,
  CalendarDays,
  Banknote,
  Users,
  Pencil,
  Trash2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { getRecentIncome } from "../services/financeService";
import { createIncomeWithStats, deleteIncomeWithStats, updateIncomeWithStats } from "../services/financeWriteService";
import { notify } from "../utils/notify";
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
} from "firebase/firestore";
import { db } from "../firebase";
import { usePanelTheme } from "../context/themeContext";
import {
  clearCachedValue,
  getCachedValue,
  setCachedValue,
} from "../utils/cache";
import StatCard from "../components/StatCard";
import UIButton from "../components/UIButton";
import PageShell from "../components/PageShell";
import PanelCard from "../components/PanelCard";
import SectionHeading from "../components/SectionHeading";

function Field({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  inputClass,
}) {
  return (
    <div>
      <label className="mb-2 block text-sm font-semibold">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={inputClass}
      />
    </div>
  );
}

function SelectField({ label, value, onChange, options, inputClass }) {
  return (
    <div>
      <label className="mb-2 block text-sm font-semibold">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={inputClass}
      >
        {options.map(([optionValue, optionLabel]) => (
          <option key={optionValue || "empty"} value={optionValue}>
            {optionLabel}
          </option>
        ))}
      </select>
    </div>
  );
}

export default function Income({ t = {}, lang = "de" }) {
  const { currentTheme, theme } = usePanelTheme();

  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [method, setMethod] = useState("Cash");
  const [client, setClient] = useState("");
  const [selectedClientId, setSelectedClientId] = useState("");
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [mobileSection, setMobileSection] = useState("form");

  const [editId, setEditId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const isGerman = lang === "de";

  const formatCurrency = (value) =>
    new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
    }).format(Number(value) || 0);

  const formatDisplayDate = (value) => {
    if (!value) return "-";
    if (typeof value?.toDate === "function") {
      return value.toDate().toLocaleDateString("de-DE");
    }
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleDateString("de-DE");
  };

  const incomeMethodLabels = {
    Cash: t.cash || "Cash",
    Bank: t.bank || (isGerman ? "Bank" : "Bankë"),
    Card: t.card || (isGerman ? "Karte" : "Kartelë"),
  };

  const getMethodBadge = (value) => {
    const dark = theme === "dark";
    const base =
      "inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold";

    if (value === "Cash") {
      return `${base} ${
        dark
          ? "bg-emerald-950 text-emerald-300"
          : "bg-emerald-100 text-emerald-700"
      }`;
    }

    if (value === "Bank") {
      return `${base} ${
        dark
          ? "bg-blue-950 text-blue-300"
          : "bg-blue-100 text-blue-700"
      }`;
    }

    if (value === "Card") {
      return `${base} ${
        dark
          ? "bg-violet-950 text-violet-300"
          : "bg-violet-100 text-violet-700"
      }`;
    }

    return `${base} ${
      dark
        ? "bg-slate-800 text-slate-300"
        : "bg-slate-100 text-slate-700"
    }`;
  };

  const inputClass = `h-12 w-full rounded-2xl border px-4 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 ${
    theme === "dark"
      ? "border-slate-700 bg-slate-900 text-slate-100 placeholder:text-slate-500"
      : "border-slate-200 bg-slate-50 text-slate-900 focus:bg-white"
  }`;

  const loadIncome = async (force = false) => {
    const clientsCacheKey = "bauheld_invoice_clients_light_v1";

    try {
      setLoading(true);

      const cachedClients = !force
        ? getCachedValue(clientsCacheKey, 10 * 60 * 1000)
        : null;

      const incomePromise = getRecentIncome(force);

      const clientsPromise = cachedClients
        ? Promise.resolve(cachedClients)
        : getDocs(
            query(
              collection(db, "invoice_clients"),
              orderBy("createdAt", "desc"),
              limit(25)
            )
          ).then((snap) => {
            const rows = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
            setCachedValue(clientsCacheKey, rows);
            return rows;
          });

      const [incomeData, clientsData] = await Promise.all([
        incomePromise,
        clientsPromise,
      ]);

      setItems(Array.isArray(incomeData) ? incomeData : []);
      setClients(Array.isArray(clientsData) ? clientsData : []);
    } catch (error) {
      console.error("Error loading income:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadIncome(false);
  }, []);

  const totalIncome = useMemo(
    () =>
      items.reduce((sum, item) => sum + (Number(item.amount) || 0), 0),
    [items]
  );

  const thisMonthIncome = useMemo(() => {
    const now = new Date();
    return items.reduce((sum, item) => {
      const d = item?.date ? new Date(item.date) : null;
      if (
        d &&
        d.getMonth() === now.getMonth() &&
        d.getFullYear() === now.getFullYear()
      ) {
        return sum + (Number(item.amount) || 0);
      }
      return sum;
    }, 0);
  }, [items]);

  const cashPayments = useMemo(
    () => items.filter((item) => item.method === "Cash").length,
    [items]
  );

  const clientPayments = useMemo(
    () => items.filter((item) => item.client || item.customer).length,
    [items]
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = items.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.max(1, Math.ceil(items.length / itemsPerPage));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const resetForm = () => {
    setAmount("");
    setDescription("");
    setDate(new Date().toISOString().slice(0, 10));
    setMethod("Cash");
    setClient("");
    setSelectedClientId("");
    setEditId(null);
  };

  const handleClientSelect = (clientId) => {
    setSelectedClientId(clientId);
    if (!clientId) {
      setClient("");
      return;
    }

    const found = clients.find((c) => c.id === clientId);
    if (found) {
      setClient(found.name || found.companyName || "");
    }
  };

  const handleEdit = (item) => {
    setEditId(item.id);
    setAmount(item.amount);
    setDescription(item.description || "");
    setDate(item.date || new Date().toISOString().slice(0, 10));
    setMethod(item.method || "Cash");
    setClient(item.client || item.customer || "");
    setSelectedClientId(item.clientId || "");
    setMobileSection("form");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (
      !window.confirm(
        isGerman
          ? "Möchten Sie dies wirklich löschen?"
          : "A jeni i sigurt që doni ta fshini?"
      )
    ) {
      return;
    }

    try {
      await deleteIncomeWithStats(id);
      notify.success(isGerman ? "Erfolgreich gelöscht" : "U fshi me sukses");
      clearCachedValue("bauheld_recent_income_v1");

      if (editId === id) resetForm();

      await loadIncome(true);

      if (currentItems.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
    } catch (error) {
      console.error("Error deleting income:", error);
      notify.error(
        isGerman ? "Fehler beim Löschen" : "Gabim gjatë fshirjes"
      );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!amount) return;

    try {
      setSaving(true);

      const entry = {
        amount: Number(amount),
        description: description || "",
        date,
        method,
        client: client || "",
        clientId: selectedClientId || "",
      };

      if (editId) {
        await updateIncomeWithStats(editId, entry);
        notify.success(
          isGerman ? "Erfolgreich aktualisiert" : "U përditësua me sukses"
        );
      } else {
        await createIncomeWithStats(entry);
        notify.success(
          t.savedSuccessfully ||
            (isGerman ? "Erfolgreich gespeichert" : "U ruajt me sukses")
        );
      }

      clearCachedValue("bauheld_recent_income_v1");
      resetForm();
      await loadIncome(true);
      setCurrentPage(1);
    } catch (error) {
      console.error("Error saving income:", error);
      notify.error(
        t.saveErrorIncome ||
          (isGerman ? "Fehler beim Speichern." : "Gabim gjatë ruajtjes.")
      );
    } finally {
      setSaving(false);
    }
  };

  const historyTitle =
    t.incomeHistoryTitle ||
    (isGerman ? "Einnahmenverlauf" : "Historia e hyrjeve");

  const recordsLabel =
    t.records || (isGerman ? "Einträge" : "regjistrime");

  const prevLabel = t.prev || (isGerman ? "Zurück" : "Mbrapa");
  const nextLabel = t.next || (isGerman ? "Weiter" : "Para");
  const pageLabel = t.page || (isGerman ? "Seite" : "Faqja");
  const ofLabel = t.of || (isGerman ? "von" : "nga");

  return (
    <PageShell>
      <SectionHeading
        title={t.incomeTitle || "Hyrje"}
        subtitle={
          t.incomeSubtitle ||
          "Regjistro të ardhurat dhe mbaj historikun financiar të pastër."
        }
      />

      <div className="grid grid-cols-2 gap-2.5 xl:grid-cols-4">
        <StatCard
          title={t.totalIncomeCard || "Totali i hyrjeve"}
          value={formatCurrency(totalIncome)}
          subtitle={t.totalIncomeCardSubtitle || "Të gjitha hyrjet"}
          icon={Wallet}
          accent="blue"
        />
        <StatCard
          title={t.thisMonth || "Ky muaj"}
          value={formatCurrency(thisMonthIncome)}
          subtitle={t.thisMonthSubtitle || "Performanca mujore"}
          icon={CalendarDays}
          accent="emerald"
        />
        <StatCard
          title={t.cashPayments || "Pagesa Cash"}
          value={cashPayments}
          subtitle={t.cashPaymentsSubtitle || "Hyrjet me para në dorë"}
          icon={Banknote}
          accent="amber"
        />
        <StatCard
          title={t.invoiceClientPresetsTitle || "Pagesa nga klientë"}
          value={clientPayments}
          subtitle={isGerman ? "Verknüpfte Kunden" : "Klientë të lidhur"}
          icon={Users}
          accent="violet"
        />
      </div>

      <div className="xl:hidden">
        <PanelCard className="p-2">
          <div className="grid grid-cols-2 gap-2">
            <UIButton
              type="button"
              onClick={() => setMobileSection("form")}
              theme={theme}
              variant={mobileSection === "form" ? "primary" : "secondary"}
              size="lg"
              className="w-full"
            >
              {editId ? t.update || "Përditëso" : t.addIncomeTitle || "Shto hyrje"}
            </UIButton>

            <UIButton
              type="button"
              onClick={() => setMobileSection("history")}
              theme={theme}
              variant={mobileSection === "history" ? "primary" : "secondary"}
              size="lg"
              className="w-full"
            >
              {t.incomeHistory || (isGerman ? "Verlauf" : "Historia")}
            </UIButton>
          </div>
        </PanelCard>
      </div>

      <div className="grid grid-cols-1 gap-6 2xl:grid-cols-[440px_minmax(0,1fr)]">
        <div className={mobileSection === "history" ? "hidden xl:block" : "block"}>
          <PanelCard>
            <div className="mb-6 flex items-center justify-between">
              <h3
                className={`text-xl font-bold tracking-tight ${
                  theme === "dark" ? "text-slate-100" : "text-slate-900"
                }`}
              >
                {editId
                  ? isGerman
                    ? "Einnahme bearbeiten"
                    : "Përditëso hyrjen"
                  : t.addIncomeTitle || "Shto hyrje"}
              </h3>

              {editId && (
                <UIButton
                  type="button"
                  onClick={resetForm}
                  theme={theme}
                  variant="softDanger"
                  size="sm"
                >
                  {t.cancel || "Anulo"}
                </UIButton>
              )}
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <SelectField
                label={t.invoiceSelectClient || "Zgjidh klientin"}
                value={selectedClientId}
                onChange={handleClientSelect}
                inputClass={inputClass}
                options={[
                  [
                    "",
                    clients.length
                      ? isGerman
                        ? "Manuell eingeben"
                        : "Shkruaj manualisht"
                      : isGerman
                      ? "Keine Kunden"
                      : "Nuk ka klientë",
                  ],
                  ...clients.map((row) => [
                    row.id,
                    `${row.name || row.companyName || "-"}${
                      row.email ? ` · ${row.email}` : ""
                    }`,
                  ]),
                ]}
              />

              <Field
                label={t.invoiceCustomer || "Klienti"}
                value={client}
                onChange={setClient}
                placeholder="P.sh. Ardit Krasniqi"
                inputClass={inputClass}
              />

              <Field
                label={t.amount || "Shuma"}
                type="number"
                value={amount}
                onChange={setAmount}
                placeholder="250.00"
                inputClass={inputClass}
              />

              <Field
                label={t.description || "Përshkrimi"}
                value={description}
                onChange={setDescription}
                placeholder={t.descriptionPlaceholder || "P.sh. Pagesë fature"}
                inputClass={inputClass}
              />

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Field
                  label={t.date || "Data"}
                  type="date"
                  value={date}
                  onChange={setDate}
                  inputClass={inputClass}
                />

                <SelectField
                  label={isGerman ? "Zahlungsmethode" : t.method || "Metoda"}
                  value={method}
                  onChange={setMethod}
                  inputClass={inputClass}
                  options={[
                    ["Cash", incomeMethodLabels.Cash],
                    ["Bank", incomeMethodLabels.Bank],
                    ["Card", incomeMethodLabels.Card],
                  ]}
                />
              </div>

              <UIButton
                type="submit"
                disabled={saving}
                theme={theme}
                variant="primary"
                size="xl"
                className="w-full"
              >
                {saving
                  ? isGerman
                    ? "Speichert..."
                    : "Duke ruajtur..."
                  : editId
                  ? t.update || "Përditëso"
                  : t.saveIncome || "Ruaj hyrjen"}
              </UIButton>
            </form>
          </PanelCard>
        </div>

        <PanelCard>
          <div className="mb-6 flex items-start justify-between gap-4">
            <h3
              className={`text-xl font-bold tracking-tight ${
                theme === "dark" ? "text-slate-100" : "text-slate-900"
              }`}
            >
              {historyTitle}
            </h3>

            <div
              className={`rounded-2xl px-3 py-2 text-sm font-semibold ${currentTheme.surfaceSoft} ${currentTheme.muted}`}
            >
              {items.length} {recordsLabel}
            </div>
          </div>

          <div className="flex-1">
            {loading ? (
              <div className="py-10 text-center text-sm">
                {t.loading || "Duke u ngarkuar..."}
              </div>
            ) : currentItems.length === 0 ? (
              <div className="py-10 text-center text-sm">
                {t.noData || "Nuk ka të dhëna."}
              </div>
            ) : (
              <div className="space-y-3">
                {currentItems.map((item, index) => (
                  <div
                    key={item.id || index}
                    className={`flex flex-col gap-4 rounded-[20px] border p-4 transition shadow-[0_12px_30px_rgba(15,23,42,0.04)] md:flex-row md:items-center md:justify-between ${
                      theme === "dark"
                        ? "border-slate-700 bg-slate-900/60 hover:border-slate-600 hover:bg-slate-900"
                        : "border-slate-200 bg-slate-50/80 hover:border-slate-300 hover:bg-white"
                    }`}
                  >
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h4
                          className={`text-base font-bold ${
                            theme === "dark" ? "text-slate-100" : "text-slate-900"
                          }`}
                        >
                          {formatCurrency(item.amount)}
                        </h4>

                        <span className={getMethodBadge(item.method)}>
                          {incomeMethodLabels[item.method] || item.method}
                        </span>
                      </div>

                      <p
                        className={`mt-1.5 text-sm font-bold ${
                          theme === "dark" ? "text-slate-300" : "text-slate-700"
                        }`}
                      >
                        {item.client || item.customer || "—"}
                      </p>

                      <p
                        className={`mt-0.5 truncate text-sm font-medium ${
                          theme === "dark" ? "text-slate-400" : "text-slate-500"
                        }`}
                      >
                        {item.description || t.noDescription || "Pa përshkrim"}
                      </p>

                      <div className={`mt-1.5 text-xs font-semibold ${currentTheme.muted}`}>
                        {formatDisplayDate(item.date || item.createdAt)}
                      </div>
                    </div>

                    <div className="mt-2 flex items-center gap-2 md:mt-0">
                      <UIButton
                        type="button"
                        onClick={() => handleEdit(item)}
                        theme={theme}
                        variant="primary"
                        size="sm"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                        <span className="hidden sm:inline">
                          {t.edit || "Ndrysho"}
                        </span>
                      </UIButton>

                      <UIButton
                        type="button"
                        onClick={() => handleDelete(item.id)}
                        theme={theme}
                        variant="softDanger"
                        size="sm"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        <span className="hidden sm:inline">
                          {t.delete || "Fshij"}
                        </span>
                      </UIButton>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between border-t border-slate-200/60 pt-5 dark:border-slate-700/60">
              <UIButton
                type="button"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                theme={theme}
                variant="secondary"
                size="md"
              >
                <ChevronLeft className="h-4 w-4" />
                <span className="hidden sm:inline">{prevLabel}</span>
              </UIButton>

              <span className={`text-sm font-semibold ${currentTheme.muted}`}>
                {pageLabel} {currentPage} {ofLabel} {totalPages}
              </span>

              <UIButton
                type="button"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                theme={theme}
                variant="secondary"
                size="md"
              >
                <span className="hidden sm:inline">{nextLabel}</span>
                <ChevronRight className="h-4 w-4" />
              </UIButton>
            </div>
          )}
        </PanelCard>
      </div>
    </PageShell>
  );
}
